import { CopyEntity, ENTITY_FIELD_ACCESSOR, ExistingMode, PREVIEW_ENTITY_LABELS, PreviewEntity } from '../types/copy-paste.types';
import { FlexRow, PreviewCard, PreviewChip } from './CopyPaste.styles';
import React, { useMemo, useState } from 'react';
import {
    formatFieldVal,
    generatePartIdentifier,
    getApplicableAlwaysCopiedEntities,
    getChangedEntities,
    getSubItemMatchKey,
    getWillSkip,
} from '../utils/copy-paste.utils';

import Button from '@ingka/button';
import { Caption, SectionLabel } from '../../../../../shared/styles/components/primitives.styles';
import type { Item } from '../../../types/item.types';
import { SectionHeader } from '../../../../../shared/styles/components/layout.styles';
import { Select } from '../../../../../shared/styles/components/forms.styles';
import { SubItem } from '../../../types/sub-item.types';
import { tokens } from '../../../../../shared/styles/core/tokens';

const PREVIEW_LIMIT = 3;

export type PartPreview =
    | { srcPart: SubItem; destPart: SubItem; variant: 'existing'; changedEntities: PreviewEntity[] }
    | { srcPart: SubItem; destPart: undefined; variant: 'new'; changedEntities: PreviewEntity[] };

interface UsePartPreviewsParams {
    copiedParts: SubItem[];
    resolvedSelectedItems: Item[];
    verifiedPartIds: string[];
    verifiedItemNos: string[];
    selectedCopyEntities: CopyEntity[];
    existingMode: ExistingMode;
    previewItemNo: string;
}

export const usePartPreviews = ({
    copiedParts,
    resolvedSelectedItems,
    verifiedPartIds,
    verifiedItemNos,
    selectedCopyEntities,
    existingMode,
    previewItemNo,
}: UsePartPreviewsParams) => {
    const selectedParts = useMemo(
        () => copiedParts.filter((p) => verifiedPartIds.includes(generatePartIdentifier(p))),
        [copiedParts, verifiedPartIds]
    );

    const previewCandidates = useMemo(
        () => resolvedSelectedItems.filter((i) => verifiedItemNos.includes(i.itemNo)),
        [resolvedSelectedItems, verifiedItemNos]
    );

    const targetItem = useMemo(
        () => previewCandidates.find((i) => i.itemNo === previewItemNo) ?? previewCandidates[0],
        [previewCandidates, previewItemNo]
    );

    const partPreviews = useMemo<PartPreview[]>(() => {
        if (!targetItem || selectedParts.length === 0) return [];
        return selectedParts.map((srcPart) => {
            const destPart = targetItem.subItems.find(
                (s) => getSubItemMatchKey(s) === getSubItemMatchKey(srcPart)
            );
            const variant = destPart ? 'existing' : 'new';
            const entities: PreviewEntity[] = [
                ...selectedCopyEntities,
                ...getApplicableAlwaysCopiedEntities(variant, existingMode),
            ];
            const changedEntities = getChangedEntities(srcPart, destPart, entities, existingMode);
            return destPart
                ? { srcPart, destPart, variant: 'existing', changedEntities }
                : { srcPart, destPart: undefined, variant: 'new', changedEntities };
        });
    }, [selectedParts, targetItem, selectedCopyEntities, existingMode]);

    return {
        previewCandidates,
        targetItem,
        partPreviews,
        hasExisting: partPreviews.some((p) => p.variant === 'existing'),
        hasNew: partPreviews.some((p) => p.variant === 'new'),
        hasPreview:
            selectedParts.length > 0 && selectedCopyEntities.length > 0 && previewCandidates.length > 0,
    };
};

interface PastePreviewProps {
    partPreviews: PartPreview[];
    previewCandidates: Item[];
    targetItem: Item;
    existingMode: ExistingMode;
    createMissing: boolean;
    canCreate: boolean;
    onChangePreviewItemNo: (itemNo: string) => void;
    isSubmitting: boolean;
}

export const PastePreview = ({
    partPreviews,
    previewCandidates,
    targetItem,
    existingMode,
    createMissing,
    canCreate,
    onChangePreviewItemNo,
    isSubmitting,
}: PastePreviewProps) => {
    const { changedPreviews, unchangedPreviews } = useMemo(() => {
        const changed: PartPreview[] = [];
        const unchanged: PartPreview[] = [];
        for (const p of partPreviews) {
            const willSkip = getWillSkip(p.variant, existingMode, createMissing, canCreate);
            const hasEffect = !willSkip && p.changedEntities.length > 0;
            (hasEffect ? changed : unchanged).push(p);
        }
        return { changedPreviews: changed, unchangedPreviews: unchanged };
    }, [partPreviews, existingMode, createMissing, canCreate]);

    const [showAllPreviews, setShowAllPreviews] = useState(false);
    const visibleChanged = showAllPreviews ? changedPreviews : changedPreviews.slice(0, PREVIEW_LIMIT);
    const visibleUnchanged = showAllPreviews ? unchangedPreviews : [];
    const visiblePreviews = [...visibleChanged, ...visibleUnchanged];

    const hiddenChangedCount = changedPreviews.length - visibleChanged.length;
    const totalHiddenCount = hiddenChangedCount + unchangedPreviews.length;

    const showMoreLabel = useMemo(() => {
        const parts: string[] = [];
        if (hiddenChangedCount > 0) parts.push(`${hiddenChangedCount} with changes`);
        if (unchangedPreviews.length > 0) parts.push(`${unchangedPreviews.length} unchanged or skipped`);
        return `Show ${totalHiddenCount} more (${parts.join(', ')})`;
    }, [hiddenChangedCount, unchangedPreviews.length, totalHiddenCount]);

    const emptyPreviewReason = useMemo(() => {
        if (changedPreviews.length > 0 || partPreviews.length === 0) return null;
        const allNew = partPreviews.every((p) => p.variant === 'new');
        const allExisting = partPreviews.every((p) => p.variant === 'existing');

        if (allNew && !canCreate) {
            return 'None of the copied parts exist on this item, and they cannot be created — only user/application-created parts can be created.';
        }
        if (allNew && !createMissing) {
            return 'None of the copied parts exist on this item. Enable "Create missing parts on target items" to create them.';
        }
        if (allExisting && existingMode === 'skip') {
            return 'All copied parts already exist on this item and "Skip existing parts" is selected, nothing will be pasted.';
        }
        if (allExisting && existingMode === 'fill-missing') {
            return 'All copied parts already exist on this item, and none of the selected fields are empty on the target (0 counts as a value), so nothing will be pasted.';
        }
        return 'No fields will change for any parts on this target item — the values already match.';
    }, [changedPreviews.length, partPreviews, canCreate, createMissing, existingMode]);

    return (
        <>
            <SectionLabel>
                Paste preview —{' '}
                {previewCandidates.length > 1 ? (
                    <Select
                        value={targetItem.itemNo}
                        onChange={(e) => onChangePreviewItemNo(e.target.value)}
                        disabled={isSubmitting}
                        style={{ width: 'auto' }}
                    >
                        {previewCandidates.map((item) => (
                            <option key={item.itemNo} value={item.itemNo}>
                                {item.itemNo} ({item.itemName})
                            </option>
                        ))}
                    </Select>
                ) : (
                    <>{targetItem.itemNo} ({targetItem.itemName})</>
                )}
            </SectionLabel>

            {emptyPreviewReason && <Caption>{emptyPreviewReason}</Caption>}

            {visiblePreviews.map((preview) => (
                <PartPreviewCard
                    key={generatePartIdentifier(preview.srcPart)}
                    preview={preview}
                    existingMode={existingMode}
                    createMissing={createMissing}
                    canCreate={canCreate}
                />
            ))}

            {totalHiddenCount > 0 && (
                <Button type='tertiary' small onClick={() => setShowAllPreviews((v) => !v)}>
                    {showAllPreviews ? 'Show less' : showMoreLabel}
                </Button>
            )}
        </>
    );
};

const PartPreviewCard = ({
    preview,
    existingMode,
    createMissing,
    canCreate,
}: {
    preview: PartPreview;
    existingMode: ExistingMode;
    createMissing: boolean;
    canCreate: boolean;
}) => {
    const { srcPart, changedEntities } = preview;
    const willSkip = getWillSkip(preview.variant, existingMode, createMissing, canCreate);
    const hasChanges = !willSkip && changedEntities.length > 0;
    const cardVariant = preview.variant === 'existing' ? 'warning' : 'success';

    const label = (() => {
        if (preview.variant === 'new') {
            if (!createMissing || !canCreate) return 'new, will be skipped';
            return hasChanges ? 'new, will be created' : 'new, no values to copy';
        }
        if (existingMode === 'skip') return 'already exists, will be skipped';
        return hasChanges ? 'already exists, will be updated' : 'already exists, no changes';
    })();

    return (
        <PreviewCard $variant={hasChanges ? cardVariant : undefined}>
            <SectionHeader style={{ fontWeight: tokens.font.weight.bold }}>
                {srcPart.part} ({srcPart.materialGrade}) — {label}
            </SectionHeader>
            {hasChanges ? (
                <FlexRow $gap='xs'>
                    {changedEntities.map((entity) => (
                        <ChangedChip
                            key={entity}
                            entity={entity}
                            srcPart={srcPart}
                            isNew={preview.variant === 'new'}
                        />
                    ))}
                </FlexRow>
            ) : !willSkip ? (
                <Caption>
                    {preview.variant === 'existing' && existingMode === 'fill-missing'
                        ? 'Selected target fields already have values, so Fill missing only will not overwrite them.'
                        : 'All selected fields already match the source values.'}
                </Caption>
            ) : null}
        </PreviewCard>
    );
};

const ChangedChip = ({
    entity,
    srcPart,
    isNew,
}: {
    entity: PreviewEntity;
    srcPart: SubItem;
    isNew: boolean;
}) => (
    <PreviewChip $changed $isNew={isNew}>
        {PREVIEW_ENTITY_LABELS[entity]}: {formatFieldVal(srcPart[ENTITY_FIELD_ACCESSOR[entity]])}
    </PreviewChip>
);
